import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaHeart, FaRegHeart, FaArrowLeft, FaArrowRight, FaMusic } from 'react-icons/fa';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { setLoading, setError } from '../../features/marketplace/marketplaceSlice';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import MarketplaceLayout from './MarketplaceLayout';
import { SEOHead } from '../SEO';
import AudioPreviewPlayer from './AudioPreviewPlayer';

// Styled components
const Container = styled.div`
  color: white;
  padding: 0 1rem;
  max-width: 1400px;
  margin: 0 auto;
  
  .related-product-image {
    transition: transform 0.3s ease;
  }
  
  .related-product-image:hover {
    transform: scale(1.05);
  }
`;

const BackButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  margin-bottom: 2rem;
  font-weight: 500;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
`;

const ProductInfo = styled.div`
  padding: 1rem;
`;

const RelatedProducts = styled.div`
  margin-top: 3rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
`;

const RelatedTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
`;

const MusicBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(90deg, rgba(74, 144, 226, 0.3), rgba(99, 179, 237, 0.3));
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1rem;
  border: 1px solid rgba(74, 144, 226, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.marketplace);
  const { addToCart } = useCart();
  
  // State declarations
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [seller, setSeller] = useState(null);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productCategory, setProductCategory] = useState('');
  
  // Fetch product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!productId) return;
      
      try {
        dispatch(setLoading(true));
        
        // Fetch product data
        const productDoc = await getDoc(doc(db, 'products', productId));
        if (!productDoc.exists()) {
          dispatch(setError('Product not found'));
          return;
        }
        
        const productData = {
          $id: productDoc.id,
          ...productDoc.data()
        };
        
        // Debug log to check product data
        console.log('Product Data:', productData);
        console.log('Is Music Product:', productData.category === 'music');
        console.log('Has Audio URL:', !!productData.audioFileUrl);
        
        setProduct(productData);
        setProductName(productData.name || '');
        setProductDescription(productData.description || '');
        setProductCategory(productData.category || '');
        
        // Fetch seller info
        if (productData.sellerId) {
          const sellerDoc = await getDoc(doc(db, 'users', productData.sellerId));
          if (sellerDoc.exists()) {
            setSeller({
              $id: sellerDoc.id,
              ...sellerDoc.data()
            });
          }
        }
        
        // Fetch related products
        const relatedQuery = query(
          collection(db, 'products'),
          where('category', '==', productData.category || ''),
          where('status', '==', 'active'),
          limit(4)
        );
        
        const relatedSnapshot = await getDocs(relatedQuery);
        const relatedItems = relatedSnapshot.docs
          .map(doc => ({
            $id: doc.id,
            ...doc.data()
          }))
          .filter(item => item.$id !== productId);
        
        setRelatedProducts(relatedItems);
        
      } catch (err) {
        console.error('Error fetching product:', err);
        dispatch(setError('Failed to load product'));
      } finally {
        dispatch(setLoading(false));
      }
    };
    
    fetchProductDetails();
    
  }, [productId, dispatch]);
  
  // Create a memoized version of the related products section
  const memoizedRelatedProducts = useMemo(() => {
    if (relatedProducts && relatedProducts.length > 0) {
      return (
        <RelatedProducts>
          <RelatedTitle>You might also like</RelatedTitle>
          <RelatedGrid>
            {relatedProducts.map(relatedProduct => (
              <motion.div 
                key={relatedProduct.$id}
                onClick={() => navigate(`/marketplace/products/${relatedProduct.$id}`)}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                style={{ 
                  cursor: 'pointer',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ 
                  position: 'relative', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  marginBottom: '10px',
                  aspectRatio: '16/9'
                }}>
                  <img 
                    src={relatedProduct.imageUrl} 
                    alt={relatedProduct.name}
                    loading="lazy"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease'
                    }}
                    className="related-product-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                </div>
                <h3 style={{ 
                  color: 'white', 
                  marginTop: '0.5rem', 
                  fontSize: '1rem', 
                  fontWeight: '500', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  marginBottom: '0.5rem'
                }}>
                  {relatedProduct.name}
                </h3>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center'
                }}>
                  <p style={{ 
                    background: 'linear-gradient(90deg, #4a90e2, #63b3ed)', 
                    WebkitBackgroundClip: 'text', 
                    WebkitTextFillColor: 'transparent', 
                    fontWeight: 'bold', 
                    margin: '0', 
                    fontSize: '1.05rem'
                  }}>
                    {parseFloat(relatedProduct.price).toLocaleString('en-UG')} UGX
                  </p>
                  <div style={{ 
                    background: 'rgba(74, 144, 226, 0.15)', 
                    borderRadius: '50%', 
                    width: '28px', 
                    height: '28px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <FaArrowRight style={{ fontSize: '0.8rem', color: '#4a90e2' }} />
                  </div>
                </div>
              </motion.div>
            ))}
          </RelatedGrid>
        </RelatedProducts>
      );
    }
    return null;
  }, [relatedProducts, navigate]);
  
  const handleAddToCart = () => {
    if (!product) return;
    
    addToCart({
      id: product.$id,
      title: product.name,
      price: product.price,
      imageUrl: product.imageUrl,
      type: 'product',
      sellerId: product.sellerId,
      quantity: 1
    });
    
    toast.success(`${product.name} added to cart`);
  };
  
  const handleGoBack = () => {
    navigate(-1);
  };
  
  if (loading) {
    return (
      <MarketplaceLayout>
        <Container>
          <p>Loading...</p>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (error) {
    return (
      <MarketplaceLayout>
        <Container>
          <p>Error: {error}</p>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (!product) {
    return (
      <MarketplaceLayout>
        <Container>
          <p>Product not found</p>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  return (
    <MarketplaceLayout>
      <SEOHead
        title={productName}
        description={productDescription || `Buy ${productName} on Konzo Marketplace`}
        keywords={`${productName}, ${productCategory}, buy music, konzo vibez, marketplace, ${seller?.displayName || ''}`}
      />
      <Container>
        <BackButton 
          onClick={handleGoBack}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft /> Back to Products
        </BackButton>
        
        <ProductGrid>
          <ImageContainer>
            <img 
              src={product.imageUrl} 
              alt={product.name}
              style={{ width: '100%', height: 'auto' }}
            />
          </ImageContainer>
          
          <ProductInfo>
            {product.category === 'music' && (
              <MusicBadge>
                <FaMusic /> Digital Music
              </MusicBadge>
            )}
            <h1>{product.name}</h1>
            <p>{product.description}</p>
            
            {/* Audio player for music products only */}
            {product.category === 'music' && (
              <div style={{ 
                marginTop: '20px', 
                marginBottom: '20px', 
                padding: '15px',
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.2), rgba(99, 179, 237, 0.1))',
                borderRadius: '8px',
                border: '2px solid rgba(74, 144, 226, 0.3)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <FaMusic style={{ color: '#4a90e2', marginRight: '8px' }} />
                  <span style={{ fontWeight: 'bold' }}>30-Second Preview</span>
                </div>
                <audio 
                  controls 
                  src={product.audioFileUrl || "https://res.cloudinary.com/demo/video/upload/v1689175244/samples/elephants.mp3"}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '10px', textAlign: 'center' }}>
                  Preview limited to 30 seconds. Purchase to listen to the full track.
                </div>
              </div>
            )}
            
            <button 
              onClick={handleAddToCart}
              style={{
                background: 'linear-gradient(90deg, #4a90e2, #63b3ed)',
                color: 'white',
                border: 'none',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                marginTop: '1.5rem',
                boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              <FaShoppingCart /> Add to Cart
            </button>
          </ProductInfo>
        </ProductGrid>
        
        {/* Use the memoized related products section */}
        {memoizedRelatedProducts}
      </Container>
    </MarketplaceLayout>
  );
};

export default ProductDetails;
